"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.exportPdfBillsToPDF = exports.exportPdfInvoicesToPDF = exports.exportSettlementToPDF = exports.exportClubInvoiceToPDF = exports.exportOrderToPDF = void 0;
exports.lightenColor = lightenColor;
const jspdf_1 = require("jspdf");
const jspdf_autotable_1 = __importDefault(require("jspdf-autotable"));
const dayjs_1 = __importDefault(require("dayjs"));
const stringFormatted_1 = require("../stringFormatted");
const COLORS = {
    dark: [16, 23, 42], // Slate-900
    darker: [11, 18, 31], // Slate-950
    accent: [59, 130, 246], // Blue-500
    accentSoft: [219, 234, 254], // Blue-100
    text: [51, 65, 85], // Slate-600
    textLight: [100, 116, 139], // Slate-500
    white: [255, 255, 255],
    bgSoft: [248, 250, 252], // Slate-50
};
function lightenColor(rgb, percent) {
    return [
        Math.round(rgb[0] + (255 - rgb[0]) * percent),
        Math.round(rgb[1] + (255 - rgb[1]) * percent),
        Math.round(rgb[2] + (255 - rgb[2]) * percent),
    ];
}
const exportOrderToPDF = (invoice, t) => {
    const doc = new jspdf_1.jsPDF({
        unit: 'pt',
        format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const currencySymbol = invoice.orderItems[0].currency === 'EUR' ? '€' : '$';
    doc.setFont('helvetica', 'normal');
    // =========================================================================
    // 1. HEADER – estilo Tech Bar
    // =========================================================================
    const headerHeight = 90;
    const SMART_BLUE = [2, 28, 71];
    const headerFill = lightenColor(SMART_BLUE, 0.9);
    doc.setFillColor(...headerFill);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');
    // Título principal
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text((t('bill') || 'BILL').toUpperCase(), margin, 50);
    let currentY = headerHeight + 25;
    const qrSize = 50;
    if (invoice.billingVerificationSystem?.qrImage) {
        const qrX = pageWidth - margin - qrSize;
        const qrY = (headerHeight - qrSize) / 2;
        doc.addImage(`data:image/png;base64,${invoice.billingVerificationSystem.qrImage}`, 'PNG', qrX, qrY, qrSize, qrSize);
        const label = invoice.billingVerificationSystem.type || 'Verification QR';
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.dark);
        const textX = qrX + qrSize / 2;
        const textY = qrY + qrSize + 14;
        doc.text(label, textX, textY, { align: 'center' });
    }
    // =========================================================================
    // 2. Issuer + CUSTOMER GRID
    // =========================================================================
    const colWidth = (pageWidth - margin * 2) / 2;
    // ---- Issuer (máquina/club) ----
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.dark);
    let issuerY = currentY + 16;
    doc.text(invoice.clubName || '-', margin, issuerY);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    const issuerLines = [
        invoice.machineName ? `${t('machine')}: ${invoice.machineName}` : '',
        invoice.operationId ? `${t('operationId')}: ${invoice.operationId}` : '',
        /*    invoice.invoiceId ? `${t('invoiceNumber')}: ${invoice.invoiceNumber}` : '',*/
    ].filter(Boolean);
    issuerY += 18;
    issuerLines.forEach((l) => {
        const pieces = doc.splitTextToSize(l, colWidth - 10);
        doc.text(pieces, margin, issuerY);
        issuerY += 14 * pieces.length;
    });
    // ---- CUSTOMER (opcional) ----
    const rightX = pageWidth - margin; // margen derecho real
    let custY = currentY + 16;
    if (invoice.customer) {
        const c = invoice.customer;
        // Nombre en negrita
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        doc.text(c.name || '—', rightX, custY, { align: 'right' });
        // Resto de líneas
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...COLORS.text);
        const custLines = [
            c.address ? formatAddress(c.address) : '',
            c.taxId ? `${t('userForm.taxId')}: ${c.taxId}` : '',
        ].filter(Boolean);
        custY += 18;
        custLines.forEach((l) => {
            const pieces = doc.splitTextToSize(l, colWidth - 10);
            pieces.forEach((p) => {
                doc.text(p, rightX, custY, { align: 'right' });
                custY += 14;
            });
        });
    }
    currentY = Math.max(issuerY, custY) + 20;
    // ======================================================================
    // ORDER PERIOD – encima de la línea azul, uno al lado del otro
    // ======================================================================
    doc.setFont('courier', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.dark);
    const orderStart = invoice.orderStartTs
        ? dayjs_1.default.unix(Number(invoice.orderStartTs)).format('DD/MM/YYYY HH:mm')
        : '-';
    const orderEnd = invoice.orderEndTs
        ? dayjs_1.default.unix(Number(invoice.orderEndTs)).format('DD/MM/YYYY HH:mm')
        : '-';
    // posición lado izquierdo
    doc.text(`${t('orderStart')}: ${orderStart}`, margin, currentY);
    // posición lado derecho
    doc.text(`${t('orderEnd')}: ${orderEnd}`, pageWidth - margin, currentY, {
        align: 'right',
    });
    currentY += 20;
    // ======================================================================
    // Separator (línea azul)
    // ======================================================================
    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(1);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 20;
    // =========================================================================
    // 3. TABLE – order items
    // =========================================================================
    (0, jspdf_autotable_1.default)(doc, {
        startY: currentY,
        head: [
            [t('productName'), t('units'), t('price'), t('total'), t('category')],
        ],
        body: invoice.orderItems.map((item) => [
            item.productName,
            item.amount,
            `${item.price.toFixed(2)} ${currencySymbol}`,
            `${item.payAmount.toFixed(2)} ${currencySymbol}`,
            t(item.category) || item.category,
        ]),
        theme: 'grid',
        styles: {
            font: 'helvetica',
            fontSize: 10,
            cellPadding: 6,
            textColor: COLORS.text,
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
        },
        headStyles: {
            fillColor: COLORS.dark,
            textColor: COLORS.white,
            fontSize: 10,
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: COLORS.bgSoft,
        },
        margin: { left: margin, right: margin },
    });
    const afterTableY = doc.lastAutoTable.finalY + 20;
    // =========================================================================
    // 4. SUMMARY CARD – igual estilo que ClubInvoice
    // =========================================================================
    const vatDetails = invoice.vatDetails || [];
    const cardWidth = 280;
    const cardX = pageWidth - margin - cardWidth;
    const initialCardY = afterTableY;
    let cardY = initialCardY;
    // dynamic height
    let simulatedY = cardY + 20;
    simulatedY += 20;
    simulatedY += 18 * (1 + vatDetails.length + 2);
    const paddingBottom = 20;
    const dynamicCardHeight = simulatedY - cardY + paddingBottom;
    doc.setFillColor(...COLORS.bgSoft);
    doc.roundedRect(cardX, cardY, cardWidth, dynamicCardHeight, 8, 8, 'F');
    cardY += 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.dark);
    doc.text(t('summary').toUpperCase(), cardX + 15, cardY);
    const writeSummary = (label, val, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...COLORS.text);
        cardY += 20;
        doc.text(label, cardX + 15, cardY);
        doc.text(val, cardX + cardWidth - 15, cardY, { align: 'right' });
    };
    writeSummary(t('taxBase'), `${invoice.totalTaxBase.toFixed(2)} ${currencySymbol}`);
    vatDetails.forEach((v) => writeSummary(`IVA ${v.vatPercent}% ${t('on')} ${v.taxBase.toFixed(2)}`, `${v.vatAmount.toFixed(2)} ${currencySymbol}`));
    // línea separadora
    cardY += 10;
    doc.setDrawColor(...COLORS.accent);
    doc.line(cardX + 15, cardY, cardX + cardWidth - 15, cardY);
    cardY += 10;
    writeSummary(t('total').toUpperCase(), `${invoice.taxedAmount.toFixed(2)} ${currencySymbol}`, true);
    // =========================================================================
    // 5. FOOTER
    // =========================================================================
    doc.setFillColor(...COLORS.accent);
    doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');
    doc.save(`order-${invoice.operationId}.pdf`);
};
exports.exportOrderToPDF = exportOrderToPDF;
const exportClubInvoiceToPDF = (clubInvoice, t) => {
    const doc = new jspdf_1.jsPDF({
        unit: 'pt', // Más preciso en diseño
        format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const currencySymbol = clubInvoice.currency === 'EUR' ? '€' : '$';
    // Base font
    doc.setFont('helvetica', 'normal');
    // =====================================================
    // 1. HEADER – TECH BAR
    // =====================================================
    const headerHeight = 90;
    const SMART_BLUE = [2, 28, 71];
    const headerFill = lightenColor(SMART_BLUE, 0.9);
    doc.setFillColor(...headerFill);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');
    // Left: Club name (big)
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(clubInvoice.clubName.toUpperCase(), margin, 50);
    let currentY = headerHeight + 25;
    // =====================================================
    // 2. ISSUER + CUSTOMER – MODERN GRID
    // =====================================================
    const colWidth = (pageWidth - margin * 2) / 2;
    const issuer = clubInvoice.issuer;
    const customer = clubInvoice.customer;
    // ----- ISSUER -----
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.dark);
    let issuerY = currentY + 16;
    doc.text(issuer?.legalName || clubInvoice.clubName, margin, issuerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    const issuerLines = [
        formatAddress(issuer?.address),
        issuer?.taxId ? `NIF: ${issuer.taxId}` : '',
    ].filter(Boolean);
    issuerY += 18;
    issuerLines.forEach((l) => {
        const pieces = doc.splitTextToSize(l, colWidth - 10);
        doc.text(pieces, margin, issuerY);
        issuerY += 14 * pieces.length;
    });
    // ----- CUSTOMER -----
    const rightX = margin + colWidth + 20;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.dark);
    let custY = currentY + 16;
    doc.text(customer?.name || '—', rightX, custY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    const customerLines = customer
        ? [
            formatAddress(customer.address),
            customer.taxId ? `NIF: ${customer.taxId}` : '',
        ]
        : [];
    custY += 18;
    customerLines.forEach((l) => {
        const pieces = doc.splitTextToSize(l, colWidth - 10);
        doc.text(pieces, rightX, custY);
        custY += 14 * pieces.length;
    });
    currentY = Math.max(issuerY, custY) + 20;
    // PdfInvoice ID + date under the header
    /* doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(...COLORS.textLight)
    doc.text(t('invoiceId'), margin, currentY)
  
    doc.setFont('courier', 'bold')
    doc.setFontSize(14)
    doc.setTextColor(...COLORS.text)
    doc.text(clubInvoice.invoiceId, margin, currentY + 16)*/
    // Date column
    //const col2X = pageWidth / 2
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text(t('date').toUpperCase(), margin, currentY);
    doc.setFont('courier', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    if (clubInvoice.createDate) {
        doc.text((0, dayjs_1.default)(clubInvoice.createDate).format('DD/MM/YYYY'), margin, currentY + 16);
    }
    currentY += 40;
    // Separator
    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(1);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 20;
    // =====================================================
    // 3. TABLE – Next-Gen Table
    // =====================================================
    (0, jspdf_autotable_1.default)(doc, {
        startY: currentY,
        head: [
            [
                t('productName'),
                t('units'),
                t('vat') + ' %',
                t('vatAmount'),
                t('taxableBase'),
            ],
        ],
        body: clubInvoice.lineItems.map((item) => [
            item.productName,
            item.quantity,
            `${item.tax.vatPercent}%`,
            `${item.tax.vatAmount.toFixed(2)} ${currencySymbol}`,
            `${item.tax.taxBase.toFixed(2)} ${currencySymbol}`,
        ]),
        theme: 'grid',
        styles: {
            font: 'helvetica',
            fontSize: 10,
            cellPadding: 6,
            textColor: COLORS.text,
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
        },
        headStyles: {
            fillColor: COLORS.dark,
            textColor: COLORS.white,
            fontSize: 10,
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: COLORS.bgSoft,
        },
        didParseCell: (data) => {
            const col = data.column.index;
            const isHeader = data.section === 'head';
            if (col === 0) {
                data.cell.styles.halign = 'left'; // Producto
            }
            else if (col === 1 || col === 2) {
                data.cell.styles.halign = 'center'; // Unidades y IVA
            }
            else if (col === 3 || col === 4) {
                data.cell.styles.halign = isHeader ? 'center' : 'right'; // VAT Amount y Taxable Base
            }
        },
        margin: { left: margin, right: margin },
    });
    const afterTableY = doc.lastAutoTable.finalY + 20;
    // =====================================================
    // 4. SUMMARY CARD – dynamic height
    // =====================================================
    //const summary = clubInvoice.financialSummary
    const vatDetails = clubInvoice.vatDetails || [];
    const cardWidth = 280;
    const cardX = pageWidth - margin - cardWidth;
    const initialCardY = afterTableY;
    let cardY = initialCardY;
    // --- CALCULAR ALTURA DINÁMICA ---
    let simulatedY = cardY + 20; // margen superior
    simulatedY += 20; // espacio título
    // sumar líneas de summary
    simulatedY += 18 * (1 + vatDetails.length + 2); // taxableBase + IVA lines + totalVAT + separator + total
    const paddingBottom = 20;
    const dynamicCardHeight = simulatedY - cardY + paddingBottom;
    // --- DIBUJAR FONDO ---
    doc.setFillColor(...COLORS.bgSoft);
    doc.roundedRect(cardX, cardY, cardWidth, dynamicCardHeight, 8, 8, 'F');
    // --- PINTAR CONTENIDO ---
    cardY += 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.dark);
    doc.text(t('summary').toUpperCase(), cardX + 15, cardY);
    cardY += 20;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    const writeSummary = (label, val, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.text(label, cardX + 15, cardY);
        doc.text(val, cardX + cardWidth - 15, cardY, { align: 'right' });
        cardY += 18;
    };
    writeSummary(t('taxableBase'), `${clubInvoice.totalTaxBase.toFixed(2)} ${currencySymbol}`);
    vatDetails.forEach((vd) => writeSummary(`IVA ${vd.vatPercent}% ${t('on')} ${vd.taxBase.toFixed(2)} ${currencySymbol}`, `${vd.vatAmount.toFixed(2)} ${currencySymbol}`));
    /* writeSummary(
      t('totalVAT'),
      `${summary.vatAmount.toFixed(2)} ${currencySymbol}`
    )*/
    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(1);
    doc.line(cardX + 15, cardY, cardX + cardWidth - 15, cardY);
    cardY += 12;
    writeSummary(t('total').toUpperCase(), `${clubInvoice.grossAmount.toFixed(2)} ${currencySymbol}`, true);
    // =====================================================
    // 5. CONTACT (Bottom anchored)
    // =====================================================
    const bottomMargin = 60;
    // Calcular la altura total del contenido del bloque
    let blockHeight = 16; // título CONTACT
    if (issuer?.email)
        blockHeight += 14;
    if (issuer?.phone)
        blockHeight += 14;
    // Y inicial anclado abajo
    let contactY = pageHeight - bottomMargin - blockHeight;
    // ----- Pintar contenido -----
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`${t('contact')}`, margin, contactY);
    contactY += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    if (issuer?.email) {
        doc.text(`${t('userForm.email')}: ${issuer.email}`, margin, contactY);
        contactY += 14;
    }
    if (issuer?.phone) {
        doc.text(`${t('userForm.phoneNumber')}: ${(0, stringFormatted_1.formatInternationalPhone)(issuer.phone)}`, margin, contactY);
    }
    // =====================================================
    // 6. FOOTER – Subtle Tech Bar
    // =====================================================
    doc.setFillColor(...COLORS.accent);
    doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');
    doc.save(`INV-${clubInvoice.invoiceId}.pdf`);
};
exports.exportClubInvoiceToPDF = exportClubInvoiceToPDF;
const exportSettlementToPDF = (settlement, t) => {
    const doc = new jspdf_1.jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const currencySymbol = settlement.currency === 'EUR' ? '€' : '$';
    const margin = 14;
    // Configuración inicial
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    // 1. Encabezado
    doc.setFontSize(15);
    doc.setTextColor(55, 65, 81);
    doc.text(`${settlement.club.name}`, margin, 20);
    doc.setFontSize(12);
    doc.text(`${t('invoice')} - ${settlement.settlementNumber}`, margin, 28);
    // 🔹 2. EMISOR A LA IZQUIERDA (Smart Padel)
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    const issuer = settlement.issuer;
    const address1 = [issuer.address.street, issuer.address.postalCode]
        .filter(Boolean)
        .join(', ');
    const address2 = [
        issuer.address.city,
        issuer.address.state,
        issuer.address.country,
    ]
        .filter(Boolean)
        .join(', ');
    const issuerInfo = [
        { text: issuer.legalName, font: 'bold' },
        address1,
        address2,
        `NIF: ${issuer.taxId}`,
        issuer.email ? `Email: ${issuer.email}` : '',
        issuer.phone ? `Tel: ${formatSpanishPhone(issuer.phone)}` : '',
    ].filter(Boolean);
    let issuerBlockHeight = 0;
    issuerInfo.forEach((line, i) => {
        const txt = typeof line === 'object' ? line.text : line;
        if (typeof line === 'object' && line.font === 'bold') {
            doc.setFont('helvetica', 'bold');
            doc.text(txt, margin, 35 + i * 5);
            doc.setFont('helvetica', 'normal');
        }
        else {
            doc.text(txt, margin, 35 + i * 5);
        }
        issuerBlockHeight = 35 + (i + 1) * 5;
    });
    // 🔹 3. PERIODO DE FACTURACIÓN (debajo del emisor)
    const billingPeriodY = issuerBlockHeight + 5;
    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128);
    doc.text(`${(0, dayjs_1.default)(settlement.billingPeriod.startDate).format('DD MMMM YYYY')} - ${(0, dayjs_1.default)(settlement.billingPeriod.endDate).format('DD MMMM YYYY')}`, margin, billingPeriodY);
    // 🔹 4. CLUB A LA DERECHA
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    const club = settlement.club;
    const clubInfo = [
        club.name,
        club.taxId,
        formatAddress(club.address),
        club.email || '',
        club.phone ? formatSpanishPhone(club.phone) : '',
    ].filter(Boolean);
    // ancho máximo para alinear derecha
    const maxClubWidth = Math.max(...clubInfo.flatMap((line) => line.split('\n').map((sub) => doc.getTextWidth(sub))));
    const clubX = pageWidth - margin - maxClubWidth - 2;
    let clubCurrentY = 35;
    clubInfo.forEach((line) => {
        line.split('\n').forEach((sub) => {
            doc.text(sub, clubX, clubCurrentY);
            clubCurrentY += 5;
        });
    });
    const clubBlockHeight = clubCurrentY - 35;
    // 🔹 5. TABLA DE TRANSACCIONES
    // Evita solapamientos tomando el bloque más bajo
    const startY = Math.max(billingPeriodY + 10, 35 + clubBlockHeight + 10, 70);
    (0, jspdf_autotable_1.default)(doc, {
        startY: startY,
        head: [
            // Encabezado principal que ocupa todas las columnas
            [
                {
                    content: `${t('transactionDetail')}`,
                    colSpan: 6, // Ocupa las 6 columnas
                    styles: {
                        fontStyle: 'bold',
                        fontSize: 14,
                        halign: 'left',
                        cellPadding: { top: 5, bottom: 5, left: 5 },
                    },
                },
            ],
            // Fila de títulos de columnas en negrita
            [
                {
                    content: `${t('concept')}`,
                    styles: { fontStyle: 'bold', fontSize: 10, textColor: [55, 65, 81] },
                },
                {
                    content: `${t('quantity')}`,
                    styles: { fontStyle: 'bold', fontSize: 10, textColor: [55, 65, 81] },
                },
                {
                    content: `${t('unitCost')}`,
                    styles: { fontStyle: 'bold', fontSize: 10, textColor: [55, 65, 81] },
                },
                {
                    content: `${t('taxableBase')}`,
                    styles: { fontStyle: 'bold', fontSize: 10, textColor: [55, 65, 81] },
                },
                {
                    content: `${t('vat')}%`,
                    styles: { fontStyle: 'bold', fontSize: 10, textColor: [55, 65, 81] },
                },
                {
                    content: `${t('total')}`,
                    styles: { fontStyle: 'bold', fontSize: 10, textColor: [55, 65, 81] },
                },
            ],
        ],
        body: [
            ...settlement.lineItems.map((item) => [
                item.productName,
                item.quantity.toString(),
                `${Number(item.productPrice).toFixed(2)} €`,
                `${Number(item.tax.taxBase).toFixed(2)} €`,
                `${item.tax.vatPercent}%`,
                `${Number(item.tax.grossAmount).toFixed(2)} €`,
            ]),
        ],
        styles: {
            fontSize: 10,
            cellPadding: 5,
            textColor: [55, 65, 81],
            fillColor: [255, 255, 255],
        },
        headStyles: {
            fontSize: 12,
            fillColor: [243, 244, 246],
            textColor: [55, 65, 81],
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: [249, 250, 251],
        },
        margin: { left: 14, right: 14 },
    });
    // 6. Resumen financiero alineado a la derecha (versión compacta)
    const finalY = doc.lastAutoTable.finalY + 20;
    // Configuración del cuadro del resumen
    const summaryBoxWidth = 120;
    const summaryBoxX = pageWidth - margin - summaryBoxWidth;
    const titleHeight = 10;
    const itemSpacing = 6;
    const totalSpacing = 8;
    // Título del resumen con fondo azul
    doc.setFillColor(2, 28, 71); // #021c47
    doc.rect(summaryBoxX, finalY - 5, summaryBoxWidth, titleHeight, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`${t('financialSummary')}`, summaryBoxX + summaryBoxWidth / 2, finalY + 1, { align: 'center' });
    // Items del resumen
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81); // gray-700
    // === VAT DETAILS (igual que en el SummaryCard) ===
    const vatDetailsLines = settlement.vatDetails?.map((vd) => {
        const taxBase = vd.taxBase.toFixed(2);
        const vatAmount = vd.vatAmount.toFixed(2);
        return `IVA ${vd.vatPercent}% en ${taxBase} ${currencySymbol}: ${vatAmount} ${currencySymbol}`;
    }) || [];
    // === SUMMARY ITEMS (orden correcto) ===
    const summaryItems = [
        `${t('taxableBase')}: ${settlement.totalTaxBase.toFixed(2)} ${currencySymbol}`,
        ...vatDetailsLines,
    ];
    // Dibujar items alineados a la derecha
    summaryItems.forEach((item, index) => {
        doc.text(item, pageWidth - margin - 10, finalY + 15 + index * itemSpacing, {
            align: 'right',
        });
    });
    // Línea separadora antes del total
    const separatorY = finalY + 15 + summaryItems.length * itemSpacing + 2;
    doc.setDrawColor(200, 200, 200);
    doc.line(pageWidth - margin - summaryBoxWidth + 10, separatorY, pageWidth - margin - 10, separatorY);
    // Total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(2, 28, 71); // #021c47
    doc.text(`${t('totalToPay')} ${Number(settlement.grossAmount).toFixed(2)} €`, pageWidth - margin - 10, separatorY + totalSpacing, { align: 'right' });
    doc.save(`settlement_${settlement.settlementNumber}.pdf`);
};
exports.exportSettlementToPDF = exportSettlementToPDF;
const PREAUTH_AMBER_BG = [255, 251, 235]; // amber-50
const PREAUTH_AMBER_FG = [146, 64, 14]; // amber-800
const PREAUTH_SUBTOTAL_BG = [248, 250, 252]; // slate-50
const PREAUTH_SUBTOTAL_FG = [100, 116, 139]; // slate-500
function buildPreauthFooterRows(adjustment, t, currencySymbol) {
    const sortedAdjustments = [...adjustment.vatBreakdown].sort((a, b) => b.vatPercent - a.vatPercent);
    const adjustmentRows = sortedAdjustments.map((row) => [
        {
            content: `${t('exceedsPreauth.adjustmentRow')} (${t('exceedsPreauth.vatTag', { percent: row.vatPercent })})`,
            colSpan: 4,
            styles: {
                halign: 'right',
                fillColor: PREAUTH_AMBER_BG,
                textColor: PREAUTH_AMBER_FG,
                fontStyle: 'bold',
            },
        },
        {
            content: `-${Math.abs(row.totalAdjustment).toFixed(2)} ${currencySymbol}`,
            styles: {
                halign: 'right',
                fillColor: PREAUTH_AMBER_BG,
                textColor: PREAUTH_AMBER_FG,
                fontStyle: 'bold',
            },
        },
    ]);
    return [
        [
            {
                content: t('exceedsPreauth.subtotal'),
                colSpan: 4,
                styles: {
                    halign: 'right',
                    fillColor: PREAUTH_SUBTOTAL_BG,
                    textColor: PREAUTH_SUBTOTAL_FG,
                },
            },
            {
                content: `${adjustment.originalAmount.toFixed(2)} ${currencySymbol}`,
                styles: {
                    halign: 'right',
                    fillColor: PREAUTH_SUBTOTAL_BG,
                    textColor: PREAUTH_SUBTOTAL_FG,
                },
            },
        ],
        ...adjustmentRows,
        [
            {
                content: t('exceedsPreauth.totalCharged'),
                colSpan: 4,
                styles: {
                    halign: 'right',
                    fillColor: COLORS.accentSoft,
                    textColor: COLORS.dark,
                    fontStyle: 'bold',
                },
            },
            {
                content: `${adjustment.chargedAmount.toFixed(2)} ${currencySymbol}`,
                styles: {
                    halign: 'right',
                    fillColor: COLORS.accentSoft,
                    textColor: COLORS.dark,
                    fontStyle: 'bold',
                },
            },
        ],
    ];
}
const exportPdfInvoicesToPDF = (invoices, t) => {
    if (!invoices || invoices.length === 0) {
        return;
    }
    const doc = new jspdf_1.jsPDF({
        unit: 'pt',
        format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    // Procesar cada factura
    invoices.forEach((invoice, index) => {
        // Si no es la primera factura, agregar una nueva página
        if (index > 0) {
            doc.addPage();
        }
        const currencySymbol = invoice.currency === 'EUR' ? '€' : '$';
        doc.setFont('helvetica', 'normal');
        // =========================================================================
        // 1. HEADER – estilo Tech Bar
        // =========================================================================
        const headerHeight = 90;
        const SMART_BLUE = [2, 28, 71];
        const headerFill = lightenColor(SMART_BLUE, 0.9);
        doc.setFillColor(...headerFill);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');
        // Título principal
        doc.setTextColor(...COLORS.dark);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text((t('bill') || 'BILL').toUpperCase(), margin, 50);
        let currentY = headerHeight + 25;
        // QR de verificación en el header
        const qrSize = 50;
        if (invoice.billingVerificationSystem?.qrImage) {
            const qrX = pageWidth - margin - qrSize;
            const qrY = (headerHeight - qrSize) / 2;
            doc.addImage(`data:image/png;base64,${invoice.billingVerificationSystem.qrImage}`, 'PNG', qrX, qrY, qrSize, qrSize);
            const label = invoice.billingVerificationSystem.type || 'Verification QR';
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.dark);
            const textX = qrX + qrSize / 2;
            const textY = qrY + qrSize + 14;
            doc.text(label, textX, textY, { align: 'center' });
        }
        // =========================================================================
        // 2. Issuer + CUSTOMER GRID
        // =========================================================================
        const colWidth = (pageWidth - margin * 2) / 2;
        // ---- Issuer (emisor) ----
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        let issuerY = currentY + 16;
        doc.text(invoice.issuer?.legalName || '-', margin, issuerY);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.text);
        const issuerLines = [
            invoice.issuer?.address ? formatAddress(invoice.issuer.address) : '',
            invoice.issuer?.taxId ? `NIF: ${invoice.issuer.taxId}` : '',
            invoice.machine?.name ? `${t('machine')}: ${invoice.machine.name}` : '',
        ].filter(Boolean);
        issuerY += 18;
        issuerLines.forEach((l) => {
            const pieces = doc.splitTextToSize(l, colWidth - 10);
            doc.text(pieces, margin, issuerY);
            issuerY += 14 * pieces.length;
        });
        // ---- CUSTOMER (cliente) ----
        const rightX = pageWidth - margin;
        let custY = currentY + 16;
        if (invoice.customer) {
            const c = invoice.customer;
            // Nombre en negrita
            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...COLORS.dark);
            doc.text(c.name || '—', rightX, custY, { align: 'right' });
            // Resto de líneas
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.text);
            const custLines = [
                c.address ? formatAddress(c.address) : '',
                c.taxId ? `${t('userForm.taxId')}: ${c.taxId}` : '',
            ].filter(Boolean);
            custY += 18;
            custLines.forEach((l) => {
                const pieces = doc.splitTextToSize(l, colWidth - 10);
                pieces.forEach((p) => {
                    doc.text(p, rightX, custY, { align: 'right' });
                    custY += 14;
                });
            });
        }
        currentY = Math.max(issuerY, custY) + 20;
        // ======================================================================
        // Invoice Number + Date
        // ======================================================================
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.textLight);
        doc.text(t('invoiceId') || 'Invoice ID', margin, currentY);
        doc.setFont('courier', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(...COLORS.text);
        doc.text(invoice.invoiceNumber || '-', margin, currentY + 16);
        // Date column
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.textLight);
        doc.text(t('date').toUpperCase(), pageWidth - margin, currentY, {
            align: 'right',
        });
        doc.setFont('courier', 'normal');
        doc.setFontSize(14);
        doc.setTextColor(...COLORS.text);
        if (invoice.createDate) {
            doc.text((0, dayjs_1.default)(invoice.createDate).format('DD/MM/YYYY'), pageWidth - margin, currentY + 16, { align: 'right' });
        }
        currentY += 40;
        // ======================================================================
        // Original Invoice reference (for rectificative invoices)
        // ======================================================================
        if (invoice.originalInvoice) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(...COLORS.textLight);
            doc.text(t('originalInvoice').toUpperCase(), margin, currentY);
            doc.setFont('courier', 'normal');
            doc.setFontSize(12);
            doc.setTextColor(...COLORS.text);
            doc.text(invoice.originalInvoice.invoiceNumber, margin, currentY + 14);
            currentY += 34;
        }
        // ======================================================================
        // Separator (línea azul)
        // ======================================================================
        doc.setDrawColor(...COLORS.accent);
        doc.setLineWidth(1);
        doc.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 20;
        // =========================================================================
        // 3. TABLE – order items
        // =========================================================================
        const preauthAdjustment = invoice.preauthAdjustment;
        const preauthFooter = preauthAdjustment
            ? buildPreauthFooterRows(preauthAdjustment, t, currencySymbol)
            : [];
        (0, jspdf_autotable_1.default)(doc, {
            startY: currentY,
            head: [
                [t('productName'), t('category'), t('units'), t('price'), t('total')],
            ],
            body: [
                ...invoice.orderItems.map((item) => [
                    item.productName,
                    t(item.category) || item.category,
                    item.amount.toString(),
                    `${item.price.toFixed(2)} ${currencySymbol}`,
                    `${item.payAmount.toFixed(2)} ${currencySymbol}`,
                ]),
                ...preauthFooter,
            ],
            theme: 'grid',
            styles: {
                font: 'helvetica',
                fontSize: 10,
                cellPadding: 6,
                textColor: COLORS.text,
                lineColor: [226, 232, 240],
                lineWidth: 0.5,
            },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: COLORS.white,
                fontSize: 10,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: COLORS.bgSoft,
            },
            margin: { left: margin, right: margin },
        });
        const afterTableY = doc.lastAutoTable.finalY + 20;
        // =========================================================================
        // 4. SUMMARY CARD – igual estilo que ClubInvoice
        // =========================================================================
        const vatDetails = invoice.vatDetails || [];
        const adjustmentRows = preauthAdjustment
            ? [...preauthAdjustment.vatBreakdown].sort((a, b) => b.vatPercent - a.vatPercent)
            : [];
        // `totalTaxBase` (and `vatDetails`) already reflect the preauth adjustment:
        // sum(originalTaxBase + taxBaseAdjustment) === totalTaxBase. The adjustment
        // rows are shown below as informational deltas, so we must NOT re-apply them
        // here (doing so double-counts the adjustment and yields a negative base).
        const summaryTotal = preauthAdjustment
            ? preauthAdjustment.chargedAmount
            : invoice.grossAmount;
        const totalLabel = preauthAdjustment
            ? t('exceedsPreauth.totalCharged').toUpperCase()
            : t('total').toUpperCase();
        const cardWidth = 280;
        const cardX = pageWidth - margin - cardWidth;
        const initialCardY = afterTableY;
        let cardY = initialCardY;
        // dynamic height: title + taxBase + per-VAT rows + per-VAT adjustments + separator + total
        // With preauth we render, per VAT group, an original row plus its adjustment
        // delta (mirroring the order-detail screen), so the count doubles.
        const vatRowCount = preauthAdjustment
            ? adjustmentRows.length * 2
            : vatDetails.length;
        let simulatedY = cardY + 20;
        simulatedY += 20;
        simulatedY += 18 * (1 + vatRowCount + 2);
        const paddingBottom = 20;
        const dynamicCardHeight = simulatedY - cardY + paddingBottom;
        doc.setFillColor(...COLORS.bgSoft);
        doc.roundedRect(cardX, cardY, cardWidth, dynamicCardHeight, 8, 8, 'F');
        cardY += 20;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...COLORS.dark);
        doc.text(t('summary').toUpperCase(), cardX + 15, cardY);
        const writeSummary = (label, val, bold = false, textColor = COLORS.text) => {
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...textColor);
            cardY += 20;
            doc.text(label, cardX + 15, cardY);
            doc.text(val, cardX + cardWidth - 15, cardY, { align: 'right' });
        };
        writeSummary(t('taxBase') || t('taxableBase'), `${invoice.totalTaxBase.toFixed(2)} ${currencySymbol}`);
        if (preauthAdjustment) {
            // Mirror the order-detail screen: per VAT group show the ORIGINAL base/amount
            // followed by the amber adjustment delta. `vatDetails` already reflect the
            // adjustment, so using them here would paint the adjusted amount as if it
            // were the original.
            adjustmentRows.forEach((row) => {
                writeSummary(`IVA ${row.vatPercent}% ${t('on')} ${row.originalTaxBase.toFixed(2)} ${currencySymbol}`, `${row.originalVatAmount.toFixed(2)} ${currencySymbol}`);
                writeSummary(t('exceedsPreauth.vatAdjustmentRow', { percent: row.vatPercent }), `${row.vatAmountAdjustment < 0 ? '-' : ''}${Math.abs(row.vatAmountAdjustment).toFixed(2)} ${currencySymbol}`, false, PREAUTH_AMBER_FG);
            });
        }
        else {
            vatDetails.forEach((v) => writeSummary(`IVA ${v.vatPercent}% ${t('on')} ${v.taxBase.toFixed(2)} ${currencySymbol}`, `${v.vatAmount.toFixed(2)} ${currencySymbol}`));
        }
        // línea separadora
        cardY += 10;
        doc.setDrawColor(...COLORS.accent);
        doc.line(cardX + 15, cardY, cardX + cardWidth - 15, cardY);
        cardY += 10;
        writeSummary(totalLabel, `${summaryTotal.toFixed(2)} ${currencySymbol}`, true);
        // =========================================================================
        // 5. CONTACT (Bottom anchored) - solo si hay información de contacto
        // =========================================================================
        const bottomMargin = 60;
        if (invoice.issuer?.email || invoice.issuer?.phone) {
            let blockHeight = 16; // título CONTACT
            if (invoice.issuer?.email)
                blockHeight += 14;
            if (invoice.issuer?.phone)
                blockHeight += 14;
            let contactY = pageHeight - bottomMargin - blockHeight;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.textLight);
            doc.text(`${t('contact')}`, margin, contactY);
            contactY += 16;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.text);
            if (invoice.issuer?.email) {
                doc.text(`${t('userForm.email')}: ${invoice.issuer.email}`, margin, contactY);
                contactY += 14;
            }
            if (invoice.issuer?.phone) {
                doc.text(`${t('userForm.phoneNumber')}: ${(0, stringFormatted_1.formatInternationalPhone)(invoice.issuer.phone)}`, margin, contactY);
            }
        }
        // =========================================================================
        // 6. FOOTER – Subtle Tech Bar
        // =========================================================================
        doc.setFillColor(...COLORS.accent);
        doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');
    });
    // Guardar el PDF con el nombre basado en la primera factura
    const firstInvoice = invoices[0];
    const fileName = firstInvoice.invoiceNumber ??
        `invoices-${firstInvoice.invoiceNumber || 'export'}.pdf`;
    doc.save(fileName);
};
exports.exportPdfInvoicesToPDF = exportPdfInvoicesToPDF;
const exportPdfBillsToPDF = (bills, clubInvoice, t) => {
    if (!bills || bills.length === 0) {
        return;
    }
    const doc = new jspdf_1.jsPDF({
        unit: 'pt',
        format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    // =========================================================================
    // PRIMERA PÁGINA: Club Invoice Detail
    // =========================================================================
    const currencySymbol = clubInvoice.currency === 'EUR' ? '€' : '$';
    doc.setFont('helvetica', 'normal');
    // Header
    const headerHeight = 90;
    const SMART_BLUE = [2, 28, 71];
    const headerFill = lightenColor(SMART_BLUE, 0.9);
    doc.setFillColor(...headerFill);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(clubInvoice.clubName.toUpperCase(), margin, 50);
    let currentY = headerHeight + 25;
    // Issuer + Customer Grid
    const colWidth = (pageWidth - margin * 2) / 2;
    const issuer = clubInvoice.issuer;
    const customer = clubInvoice.customer;
    // Issuer
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.dark);
    let issuerY = currentY + 16;
    doc.text(issuer?.legalName || clubInvoice.clubName, margin, issuerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    const issuerLines = [
        formatAddress(issuer?.address),
        issuer?.taxId ? `NIF: ${issuer.taxId}` : '',
    ].filter(Boolean);
    issuerY += 18;
    issuerLines.forEach((l) => {
        const pieces = doc.splitTextToSize(l, colWidth - 10);
        doc.text(pieces, margin, issuerY);
        issuerY += 14 * pieces.length;
    });
    // Customer
    const rightX = margin + colWidth + 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.dark);
    let custY = currentY + 16;
    doc.text(customer?.name || '—', rightX, custY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    const customerLines = customer
        ? [
            formatAddress(customer.address),
            customer.taxId ? `NIF: ${customer.taxId}` : '',
        ]
        : [];
    custY += 18;
    customerLines.forEach((l) => {
        const pieces = doc.splitTextToSize(l, colWidth - 10);
        doc.text(pieces, rightX, custY);
        custY += 14 * pieces.length;
    });
    currentY = Math.max(issuerY, custY) + 20;
    // Date
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text(t('date').toUpperCase(), margin, currentY);
    doc.setFont('courier', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    if (clubInvoice.createDate) {
        doc.text((0, dayjs_1.default)(clubInvoice.createDate).format('DD/MM/YYYY'), margin, currentY + 16);
    }
    currentY += 40;
    // Separator
    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(1);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 20;
    // Table
    (0, jspdf_autotable_1.default)(doc, {
        startY: currentY,
        head: [
            [
                t('productName'),
                t('units'),
                t('vat') + ' %',
                t('vatAmount'),
                t('taxableBase'),
            ],
        ],
        body: clubInvoice.lineItems.map((item) => [
            item.productName,
            item.quantity,
            `${item.tax.vatPercent}%`,
            `${item.tax.vatAmount.toFixed(2)} ${currencySymbol}`,
            `${item.tax.taxBase.toFixed(2)} ${currencySymbol}`,
        ]),
        theme: 'grid',
        styles: {
            font: 'helvetica',
            fontSize: 10,
            cellPadding: 6,
            textColor: COLORS.text,
            lineColor: [226, 232, 240],
            lineWidth: 0.5,
        },
        headStyles: {
            fillColor: COLORS.dark,
            textColor: COLORS.white,
            fontSize: 10,
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: COLORS.bgSoft,
        },
        margin: { left: margin, right: margin },
    });
    let afterTableY = doc.lastAutoTable.finalY + 20;
    // Summary Card
    const vatDetails = clubInvoice.vatDetails || [];
    const cardWidth = 280;
    const cardX = pageWidth - margin - cardWidth;
    let cardY = afterTableY;
    let simulatedY = cardY + 20;
    simulatedY += 20;
    simulatedY += 18 * (1 + vatDetails.length + 2);
    const paddingBottom = 20;
    const dynamicCardHeight = simulatedY - cardY + paddingBottom;
    doc.setFillColor(...COLORS.bgSoft);
    doc.roundedRect(cardX, cardY, cardWidth, dynamicCardHeight, 8, 8, 'F');
    cardY += 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.dark);
    doc.text(t('summary').toUpperCase(), cardX + 15, cardY);
    const writeSummary = (label, val, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(11);
        doc.setTextColor(...COLORS.text);
        cardY += 20;
        doc.text(label, cardX + 15, cardY);
        doc.text(val, cardX + cardWidth - 15, cardY, { align: 'right' });
    };
    writeSummary(t('taxableBase'), `${clubInvoice.totalTaxBase.toFixed(2)} ${currencySymbol}`);
    vatDetails.forEach((vd) => writeSummary(`IVA ${vd.vatPercent}% ${t('on')} ${vd.taxBase.toFixed(2)} ${currencySymbol}`, `${vd.vatAmount.toFixed(2)} ${currencySymbol}`));
    cardY += 10;
    doc.setDrawColor(...COLORS.accent);
    doc.line(cardX + 15, cardY, cardX + cardWidth - 15, cardY);
    cardY += 10;
    writeSummary(t('total').toUpperCase(), `${clubInvoice.grossAmount.toFixed(2)} ${currencySymbol}`, true);
    // Contact
    const bottomMargin = 60;
    let blockHeight = 16;
    if (issuer?.email)
        blockHeight += 14;
    if (issuer?.phone)
        blockHeight += 14;
    let contactY = pageHeight - bottomMargin - blockHeight;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`${t('contact')}`, margin, contactY);
    contactY += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    if (issuer?.email) {
        doc.text(`${t('userForm.email')}: ${issuer.email}`, margin, contactY);
        contactY += 14;
    }
    if (issuer?.phone) {
        doc.text(`${t('userForm.phoneNumber')}: ${(0, stringFormatted_1.formatInternationalPhone)(issuer.phone)}`, margin, contactY);
    }
    // Footer
    doc.setFillColor(...COLORS.accent);
    doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');
    // =========================================================================
    // PÁGINAS SIGUIENTES: PdfBills
    // =========================================================================
    bills.forEach((bill) => {
        doc.addPage();
        const billCurrency = bill.currency === 'EUR' ? '€' : '$';
        doc.setFont('helvetica', 'normal');
        // Header
        doc.setFillColor(...headerFill);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');
        doc.setTextColor(...COLORS.dark);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text((t('bill') || 'BILL').toUpperCase(), margin, 50);
        currentY = headerHeight + 25;
        // QR
        const qrSize = 50;
        if (bill.billingVerificationSystem?.qrImage) {
            const qrX = pageWidth - margin - qrSize;
            const qrY = (headerHeight - qrSize) / 2;
            doc.addImage(`data:image/png;base64,${bill.billingVerificationSystem.qrImage}`, 'PNG', qrX, qrY, qrSize, qrSize);
            const label = bill.billingVerificationSystem.type || 'Verification QR';
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.dark);
            const textX = qrX + qrSize / 2;
            const textY = qrY + qrSize + 14;
            doc.text(label, textX, textY, { align: 'center' });
        }
        // Issuer + Customer
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.dark);
        issuerY = currentY + 16;
        doc.text(bill.issuer?.legalName || '-', margin, issuerY);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.text);
        const billIssuerLines = [
            bill.issuer?.address ? formatAddress(bill.issuer.address) : '',
            bill.issuer?.taxId ? `NIF: ${bill.issuer.taxId}` : '',
            bill.machine?.name ? `${t('machine')}: ${bill.machine.name}` : '',
        ].filter(Boolean);
        issuerY += 18;
        billIssuerLines.forEach((l) => {
            const pieces = doc.splitTextToSize(l, colWidth - 10);
            doc.text(pieces, margin, issuerY);
            issuerY += 14 * pieces.length;
        });
        const billRightX = pageWidth - margin;
        custY = currentY + 16;
        if (bill.customer) {
            const c = bill.customer;
            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...COLORS.dark);
            doc.text(c.name || '—', billRightX, custY, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.text);
            const custLines = [
                c.address ? formatAddress(c.address) : '',
                c.taxId ? `${t('userForm.taxId')}: ${c.taxId}` : '',
            ].filter(Boolean);
            custY += 18;
            custLines.forEach((l) => {
                const pieces = doc.splitTextToSize(l, colWidth - 10);
                pieces.forEach((p) => {
                    doc.text(p, billRightX, custY, { align: 'right' });
                    custY += 14;
                });
            });
        }
        currentY = Math.max(issuerY, custY) + 20;
        // Invoice Number + Date
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.textLight);
        doc.text(t('invoiceId') || 'Invoice ID', margin, currentY);
        doc.setFont('courier', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(...COLORS.text);
        doc.text(bill.invoiceNumber || '-', margin, currentY + 16);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.textLight);
        doc.text(t('date').toUpperCase(), pageWidth - margin, currentY, {
            align: 'right',
        });
        doc.setFont('courier', 'normal');
        doc.setFontSize(14);
        doc.setTextColor(...COLORS.text);
        if (bill.createDate) {
            doc.text((0, dayjs_1.default)(bill.createDate).format('DD/MM/YYYY'), pageWidth - margin, currentY + 16, { align: 'right' });
        }
        currentY += 40;
        // Separator
        doc.setDrawColor(...COLORS.accent);
        doc.setLineWidth(1);
        doc.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 20;
        // Table
        (0, jspdf_autotable_1.default)(doc, {
            startY: currentY,
            head: [
                [t('productName'), t('units'), t('price'), t('total'), t('category')],
            ],
            body: bill.orderItems.map((item) => [
                item.productName,
                item.amount.toString(),
                `${item.price.toFixed(2)} ${billCurrency}`,
                `${item.payAmount.toFixed(2)} ${billCurrency}`,
                t(item.category) || item.category,
            ]),
            theme: 'grid',
            styles: {
                font: 'helvetica',
                fontSize: 10,
                cellPadding: 6,
                textColor: COLORS.text,
                lineColor: [226, 232, 240],
                lineWidth: 0.5,
            },
            headStyles: {
                fillColor: COLORS.dark,
                textColor: COLORS.white,
                fontSize: 10,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: COLORS.bgSoft,
            },
            margin: { left: margin, right: margin },
        });
        afterTableY = doc.lastAutoTable.finalY + 20;
        // Summary Card
        const billVatDetails = bill.vatDetails || [];
        cardY = afterTableY;
        simulatedY = cardY + 20;
        simulatedY += 20;
        simulatedY += 18 * (1 + billVatDetails.length + 2);
        const billDynamicCardHeight = simulatedY - cardY + paddingBottom;
        doc.setFillColor(...COLORS.bgSoft);
        doc.roundedRect(cardX, cardY, cardWidth, billDynamicCardHeight, 8, 8, 'F');
        cardY += 20;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...COLORS.dark);
        doc.text(t('summary').toUpperCase(), cardX + 15, cardY);
        const writeBillSummary = (label, val, bold = false) => {
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.text);
            cardY += 20;
            doc.text(label, cardX + 15, cardY);
            doc.text(val, cardX + cardWidth - 15, cardY, { align: 'right' });
        };
        writeBillSummary(t('taxBase') || t('taxableBase'), `${bill.totalTaxBase.toFixed(2)} ${billCurrency}`);
        billVatDetails.forEach((v) => writeBillSummary(`IVA ${v.vatPercent}% ${t('on')} ${v.taxBase.toFixed(2)} ${billCurrency}`, `${v.vatAmount.toFixed(2)} ${billCurrency}`));
        cardY += 10;
        doc.setDrawColor(...COLORS.accent);
        doc.line(cardX + 15, cardY, cardX + cardWidth - 15, cardY);
        cardY += 10;
        writeBillSummary(t('total').toUpperCase(), `${bill.grossAmount.toFixed(2)} ${billCurrency}`, true);
        // Contact
        if (bill.issuer?.email || bill.issuer?.phone) {
            blockHeight = 16;
            if (bill.issuer?.email)
                blockHeight += 14;
            if (bill.issuer?.phone)
                blockHeight += 14;
            contactY = pageHeight - bottomMargin - blockHeight;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.textLight);
            doc.text(`${t('contact')}`, margin, contactY);
            contactY += 16;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.text);
            if (bill.issuer?.email) {
                doc.text(`${t('userForm.email')}: ${bill.issuer.email}`, margin, contactY);
                contactY += 14;
            }
            if (bill.issuer?.phone) {
                doc.text(`${t('userForm.phoneNumber')}: ${(0, stringFormatted_1.formatInternationalPhone)(bill.issuer.phone)}`, margin, contactY);
            }
        }
        // Footer
        doc.setFillColor(...COLORS.accent);
        doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');
    });
    const firstBill = bills[0];
    if (firstBill.invoiceNumber) {
        const baseInvoiceNumber = firstBill.invoiceNumber.replace(/\/[^/]+$/, '');
        const fileName = `${baseInvoiceNumber || 'export'}.pdf`;
        return doc.save(fileName);
    }
    doc.save(`club-invoice-${clubInvoice.invoiceId}-bills.pdf`);
};
exports.exportPdfBillsToPDF = exportPdfBillsToPDF;
// --- Funciones de Ayuda (Helpers) ---
// Estas funciones se mantienen como estaban, ya que son útiles.
function formatAddress(address) {
    if (!address)
        return '';
    // Línea 1: calle y código postal
    const line1 = [address.street, address.postalCode].filter(Boolean).join(', ');
    // Línea 2: provincia, ciudad y país
    const line2 = [address.state, address.city, address.country]
        .filter(Boolean)
        .join(', ');
    // Unir con salto de línea para PDF
    return [line1, line2].filter(Boolean).join('\n');
}
function formatSpanishPhone(phone) {
    if (!phone)
        return '';
    let digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('34')) {
        digits = digits.slice(2);
    }
    if (digits.length === 9) {
        return `+34 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
    }
    return phone;
}
